import { App, AppLogo, appMetadata } from "@teamworksdev/react";
import type { Person } from "./services/people";

export const TOAST_CONFIRM_DURATION = 3000;
export const isProduction = process.env.VITE_AXLE_ENV === "production";

export const isGovCloud = process.env.VITE_ENABLE_FEDCON_BANNER === "true";

export const appNames = Object.entries(appMetadata).reduce<Partial<Record<App, string>>>(
  (namesMap, [app, meta]) => {
    // TODO: Add these to Admin UI when ready
    if ([App.collectives].includes(app as App)) {
      return namesMap;
    }

    return { ...namesMap, [app]: meta.name };
  },
  {
    axle: "Axle",
  },
);

// Get the App List from the React component based on the appNames keys
export const apps = Object.values(App)
  .filter((app) => app in appNames)
  .sort((a, b) => appNames[a]?.localeCompare(appNames[b] ?? "") || 0);

export const customerFacingApps = apps.filter((app) => ![App.axle, App.recruiting].includes(app));

export const impersonationAllowedApps = [App.ams, App.gm, App.hub, App.inflcr] as const;
export type ImpersonationAllowedApp = (typeof impersonationAllowedApps)[number];
export const isImpersonationAllowedApp = (app: any): app is ImpersonationAllowedApp =>
  impersonationAllowedApps.includes(app);

export const getInfluencerBaseUrl = (): string => {
  const host = typeof window !== "undefined" ? window.location.hostname : "";
  return host.endsWith(".co.nz") ? "https://app.inflcr.au" : "https://app.inflcr.com";
};

export const buildInfluencerImpersonationUrl = (inflcrProfileId: string): string => {
  // UUID is the part before the underscore; strip the suffix.
  const actingAs = inflcrProfileId.split("_")[0];
  return `${getInfluencerBaseUrl()}/login/impersonate?acting_as=${actingAs}`;
};

export const markets = ["North America Sport", "International Sport", "Tactical"] as const;

export const customerMarkets = [
  "DI Collegiate",
  "Non-DI Collegiate",
  "NA Pro",
  "Enterprise",
  "Intl Pro",
  "Tactical",
] as const;

export const customerRegions = ["APAC", "EMEA", "LATAM", "US/CA"] as const;

export const customerLeagues = [
  "A-League",
  "AFL",
  "Agencies",
  "Argentine Primera División",
  "Australian Defence Force (inc. Reserves)",
  "Belgian Pro League 1 & 2",
  "Brasileirao",
  "Bundesliga 1 / 2",
  "Canadian Armed Forces (inc. Reserves)",
  "CFL",
  "CHL",
  "Civil Service",
  "Collective",
  "Collegiate Other",
  "Conference Governing Body",
  "Cricket",
  "DII",
  "DIII",
  "Dominican Pro Baseball League",
  "Dutch Eredivisie 1 & 2",
  "EFL Championship",
  "EFL League 1 & 2",
  "Enterprise - Campus",
  "Enterprise Other",
  "EPL",
  "Esports",
  "Events",
  "FBS Independent",
  "FCS",
  "Fire",
  "Football Other",
  "G5",
  "High School",
  "International Other",
  "IPL",
  "La Liga 1 / 2",
  "League Governing Body",
  "Liga MX",
  "Ligue 1",
  "Ligue 1 / 2",
  "Media",
  "Military",
  "MLB",
  "MLR",
  "MLS",
  "Motorsport",
  "N/A",
  "NA Pro Other",
  "NAIA",
  "National Governing Body",
  "National Law Enforcement Agencies and Police Tactical Units",
  "National Olympic Committee",
  "NBA",
  "NBL",
  "NFL",
  "NHL",
  "NJCAA",
  "NLL",
  "No-FB",
  "Non-FB",
  "NPB",
  "NRL",
  "NWSL",
  "Other",
  "Other Military",
  "Other Non-military",
  "P4",
  "Pac-12",
  "Performance Centers/Research Labs",
  "Police",
  "Premiership Rugby",
  "Rugby League Other",
  "Rugby Union",
  "Saudi Pro League/UAE Pro League",
  "Scottish Premiership",
  "Serie A / B",
  "Soccer Other",
  "Tactical",
  "Touring Events",
  "US Air Force / US Space Force (incl. Reserves & National Guard)",
  "US Army (incl. Reserves & National Guard)",
  "US Marine Corps (incl. Reserves)",
  "US Navy (incl. Reserves)",
  "USL",
  "USSOCOM",
  "WNBA",
  "Youth Sports",
] as const;

export const customerConferences = [
  "A-10",
  "ACC",
  "Alabama Community College Conference",
  "Allegheny Mountain Collegiate Conference",
  "America East",
  "American Midwest Conference",
  "American Rivers Conference",
  "American Southwest Conference",
  "American",
  "Appalachian Athletic Conference",
  "Arizona Community College Athletic Conference",
  "Arrowhead Conference",
  "ASUN",
  "Atlantic East Conference",
  "Bi-State Conference",
  "Big 12",
  "Big East",
  "Big Sky",
  "Big South",
  "Big Ten",
  "Big West",
  "CAA",
  "California Collegiate Athletic Association",
  "California Pacific Conference",
  "Capital Athletic Conference",
  "Carolinas Junior College Conference",
  "Cascade Collegiate Conference",
  "Centennial Conference",
  "Central Atlantic Collegiate Conference",
  "Central Intercollegiate Athletic Association (CIAA)",
  "CHA",
  "Chicagoland Collegiate Athletic Conference",
  "City University of New York Athletic Conference",
  "Coast to Coast Athletic Conference",
  "College Conference of Illinois and Wisconsin",
  "Collegiate Conference of the South",
  "Colonial States Athletic Conference",
  "Commonwealth Coast Conference",
  "Conference Carolinas",
  "Conference USA",
  "Crossroads League",
  "East Coast Conference",
  "ECAC",
  "Empire 8 Conference",
  "FBS Independent",
  "FCS Independent",
  "Formula Racing",
  "Frontier Conference",
  "Garden State Athletic Conference",
  "Georgia Collegiate Athletic Association",
  "Golden State Athletic Conference",
  "Great American Conference",
  "Great Lakes Intercollegiate Athletic Conference",
  "Great Lakes Valley Conference",
  "Great Midwest Athletic Conference",
  "Great Northeast Athletic Conference",
  "Great Northwest Athletic Conference",
  "Great Plains Athletic Conference",
  "Great Rivers Athletic Conference",
  "Great Southwest Athletic Conference",
  "Gulf Coast Athletic Conference",
  "Gulf South Conference",
  "Heart of America Athletic Conference",
  "Heartland Collegiate Athletic Conference",
  "Horizon",
  "Illinois Skyway Conference",
  "IndyCar",
  "Intermountain Collegiate Athletic Conference",
  "Iowa Community College Athletic Conference",
  "Ivy",
  "Kansas Collegiate Athletic Conference",
  "Kansas Jayhawk Community College Conference",
  "Landmark Conference",
  "Liberty League",
  "Little East Conference",
  "Lone Star Conference",
  "Louisiana Community Colleges Athletic Conference",
  "MAAC",
  "MAC Commonwealth",
  "MAC Freedom",
  "MAC",
  "Maryland Junior College Athletic Conference",
  "Massachusetts Community College Athletic Association",
  "Massachusetts State Collegiate Athletic Conference",
  "MEAC",
  "Metro Athletic Conference",
  "Michigan Community College Athletic Association",
  "Michigan Intercollegiate Athletic Association",
  "Mid Hudson Conference",
  "Mid-America Intercollegiate Athletics Association",
  "Middle Atlantic Conference",
  "Mid-Florida Conference",
  "Mid-South Conference",
  "Mid-State Athletic Conference",
  "Mid-States Football Association",
  "Mid-West Athletic Conference",
  "Midwest Conference",
  "Midwest Football Conference (NJCAA)",
  "Minnesota College Athletic Conference",
  "Minnesota Intercollegiate Athletic Conference",
  "Mississippi Association of Community & Junior Colleges",
  "Missouri Community College Athletic Conference",
  "Mon-Dak Conference",
  "Mountain East Conference",
  "Mountain West",
  "MVC",
  "N/A",
  "NAIA Independents",
  "NASCAR",
  "New England Collegiate Conference",
  "New England Small College Athletic Conference",
  "New England Women's and Men's Athletic Conference",
  "New Jersey Athletic Conference",
  "NJCAA Region 8",
  "North Atlantic Conference",
  "North Central Community College Conference",
  "North Coast Athletic Conference",
  "North Eastern Athletic Conference",
  "North Star Athletic Association",
  "North Texas Junior College Athletic Conference",
  "Northeast JC Football Conference",
  "Northeast-10 Conference",
  "Northeast",
  "Northern Athletics Collegiate Conference",
  "Northern Sun Intercollegiate Conference",
  "Northwest Conference",
  "Ohio Athletic Conference",
  "Ohio Community College Athletic Conference",
  "Old Dominion Athletic Conference",
  "OVC",
  "Ownership Group",
  "Pac-12",
  "Pacific West Conference",
  "Panhandle Conference",
  "Patriot",
  "Peach Belt Conference",
  "Pennsylvania Collegiate Athletic Association",
  "Pennsylvania State Athletic Conference",
  "Presidents' Athletic Conference",
  "Red River Athletic Conference",
  "River States Conference",
  "Rocky Mountain Athletic Conference",
  "Scenic West Athletic Conference",
  "SEC",
  "Skyline Conference",
  "SoCon",
  "Sooner Athletic Conference",
  "South Atlantic Conference",
  "Southern Athletic Association",
  "Southern California Intercollegiate Athletic Conference",
  "Southern Collegiate Athletic Conference",
  "Southern Conference (NJCAA)",
  "Southern Intercollegiate Athletic Conference",
  "Southern States Athletic Conference",
  "Southland",
  "Southwest Junior College Conference",
  "Southwest Junior College Football Conference",
  "St. Louis Intercollegiate Athletic Conference",
  "State University of New York Athletic Conference",
  "Summit",
  "Sun Belt",
  "Sun Conference",
  "Suncoast Conference",
  "Sunshine State Conference",
  "SWAC",
  "Tennessee Junior and Community College Athletic Association",
  "United Athletic Conference",
  "United East Conference",
  "University Athletic Association",
  "Upper Midwest Athletic Conference",
  "USA South Athletic Conference",
  "WAC",
  "WCC",
  "WCHA",
  "Western Junior College Athletic Conference",
  "Western States Football League",
  "Wisconsin Intercollegiate Athletic Conference",
  "Wolverine-Hoosier Athletic Conference",
] as const;

const config = {
  app: {
    baseUrl: process.env.VITE_BASE_FOLDER,
  },
  api: {
    baseUrl: process.env.VITE_AXLE_API_URL || "https://axle.teamworksuat.com",
    documents: {
      view: "/documents/view",
      altkey: "/documents/altkey",
      create: "/documents",
      upsert: "/documents/upsert",
      partialMatchesRoot: "/documents/partial_matches",
      partialMatchesByApp: "/documents/partial_matches/by_app",
      partialMatchesByOrg: "/documents/partial_matches/by_org",
      partialMatchDetail: "/documents/partial_matches/detail",
      partialMatchCountsByApp: "/documents/partial_matches/count/by_app",
      partialMatchCountsByOrg: "/documents/partial_matches/count/by_org",
      profileUpsert: "/documents/profile_upsert",
      profileUpsertReview: "/documents/profile_upsert/review",
      profileUpsertReviewBulk: "/documents/profile_upsert/review/bulk",
    },
    duplicateProfiles: "/api/v1/profiles/orgs", // + /{orgId}/profiles/duplicates
  },
  flags: {
    org: {
      link: true,
      unlink: false,
    },
    team: {
      link: true,
      unlink: true,
    },
    person: {
      link: true,
      unlink: true,
    },
  },
  paths: {
    login: "/",
    loginCallback: "/login/callback",
    app: "/",
    orgs: "/orgs",
    people: "/people",
    partialMatches: "/partial-matches",
    partialMatchesByOrg: "/partial-matches-by-org",
    partialMatchesByApp: "/partial-matches-by-app",
    teams: "/teams",
    users: "/users",
  },
  siteName: "Axle",
  makeOrgScope: (app: App) => `${app}.org` as `${App}.org`,
  makePersonScope: (app: App) => `${app}.profile` as `${App}.profile`,
  makeTeamScope: (app: App) => `${app}.team` as `${App}.team`,
  makeUserTypeScope: (app: App) => `${app}.user_type` as `${App}.user_type`,
} as const;

export const getMergeDocumentUrl = (id: string) => `/documents/${id}/merge`;
export const getUnmergeDocumentUrl = (id: string) => `/documents/${id}/unmerge`;

export const getPersonUpdateUrl = (id: string) => `/documents/${id}/axle.profile/partial`;

export const getPersonStatusUrl = (id: string, app: App) => `/documents/${id}/${app}.profile/partial`;

export const getOrgStatusUrl = (id: string, app: App) => `/documents/${id}/${app}.org/partial`;

export const getOrgUpdateUrl = (id: string) => `/documents/${id}/axle.org/partial`;

export const getTeamStatusUrl = (id: string, app: App) => `/documents/${id}/${app}.team/partial`;

export const getTeamUpdateUrl = (id: string) => `/documents/${id}/axle.team/partial`;

export const getPersonName = (person?: Pick<Person, "preferredName" | "givenName" | "familyName"> | null) => {
  if (!person) return "";

  const firstName = person.preferredName || person.givenName || "";
  const lastName = person.familyName || "";
  return `${firstName} ${lastName}`;
};

export const getProductIcon = (app: Partial<App> | null, size?: number) => {
  if (!app) return null;

  return <AppLogo app={app} size={size} variant="black" />;
};

export default config;
